import React, { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useProgram } from '@/hooks/useProgram'
import { apiClient } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
	Loader2,
	Plus,
	Tag as TagIcon,
	Edit2,
	Trash2,
	Check,
	X,
	Sparkles,
	Palette,
	Zap,
	Keyboard,
} from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { useProgramContext } from '@/context/ProgramContext'
import { useTranslation } from '@/hooks/useTranslation'

interface Tag {
	id: string
	name: string
	color: string | null
	program_id: string
}

const getTagColorPresets = (t: (key: string) => string) => [
	{ color: '#EF4444', name: t('tagsSettingsTab.red') },
	{ color: '#F59E0B', name: t('tagsSettingsTab.amber') },
	{ color: '#10B981', name: t('tagsSettingsTab.green') },
	{ color: '#3B82F6', name: t('tagsSettingsTab.blue') },
	{ color: '#8B5CF6', name: t('tagsSettingsTab.purple') },
	{ color: '#6366F1', name: t('tagsSettingsTab.indigo') },
	{ color: '#EC4899', name: t('tagsSettingsTab.pink') },
	{ color: '#14B8A6', name: t('tagsSettingsTab.teal') },
	{ color: '#F97316', name: t('tagsSettingsTab.orange') },
	{ color: '#84CC16', name: t('tagsSettingsTab.lime') },
	{ color: '#06B6D4', name: t('tagsSettingsTab.cyan') },
	{ color: '#6B7280', name: t('tagsSettingsTab.gray') },
]

const getSmartColorSuggestion = (tagName: string, tagColorPresets: Array<{ color: string; name: string }>): string => {
	const name = tagName.toLowerCase()
	if (
		name.includes('bug') ||
		name.includes('critical') ||
		name.includes('urgent')
	)
		return tagColorPresets[0].color
	if (name.includes('feature') || name.includes('enhancement'))
		return tagColorPresets[4].color
	if (name.includes('backend') || name.includes('api'))
		return tagColorPresets[5].color
	if (name.includes('ui') || name.includes('design'))
		return tagColorPresets[6].color
	if (name.includes('performance') || name.includes('optimize'))
		return tagColorPresets[7].color
	if (name.includes('docs') || name.includes('documentation'))
		return tagColorPresets[9].color
	if (name.includes('test') || name.includes('qa'))
		return tagColorPresets[2].color
	return tagColorPresets[Math.floor(Math.random() * tagColorPresets.length)]
		.color
}

const ColorDot = ({ color }: { color: string | null }) => (
	<motion.div
		className="w-5 h-5 rounded-full border-2 border-white shadow-md ring-1 ring-black/10"
		style={{ backgroundColor: color || '#6B7280' }}
		whileHover={{ scale: 1.1 }}
		transition={{ duration: 0.2 }}
	/>
)

const TagPreview = ({ name, color, t }: { name: string; color: string; t: (key: string) => string }) => (
	<motion.div
		initial={{ opacity: 0, scale: 0.8 }}
		animate={{ opacity: 1, scale: 1 }}
		className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-white shadow-sm"
		style={{ backgroundColor: color }}>
		<div className="w-2 h-2 rounded-full bg-white/30" />
		{name || t('tagsSettingsTab.tagPreview')}
	</motion.div>
)

const QuickColorSelector = ({
	value,
	onChange,
	disabled = false,
	forceOpenPicker = false,
	onPickerOpenChange,
	t,
	tagColorPresets,
}: {
	value: string
	onChange: (color: string) => void
	disabled?: boolean
	forceOpenPicker?: boolean
	onPickerOpenChange?: (open: boolean) => void
	t: (key: string) => string
	tagColorPresets: Array<{ color: string; name: string }>
}) => (
	<div className="flex items-center gap-1 p-1 bg-muted/30 rounded-lg">
		{tagColorPresets.slice(0, 6).map(({ color, name }, index) => (
			<Tooltip key={color}>
				<TooltipTrigger asChild>
					<motion.button
						className={cn(
							'w-6 h-6 rounded-full border-2 transition-all relative',
							value === color
								? 'border-primary ring-2 ring-primary/20 scale-110'
								: 'border-white/50 hover:border-primary/50 hover:scale-105'
						)}
						style={{ backgroundColor: color }}
						onClick={() => onChange(color)}
						disabled={disabled}
						whileHover={{ scale: 1.1 }}
						whileTap={{ scale: 0.95 }}>
						{value === color && (
							<motion.div
								initial={{ scale: 0 }}
								animate={{ scale: 1 }}
								className="absolute inset-0 flex items-center justify-center">
								<Check className="w-3 h-3 text-white drop-shadow" />
							</motion.div>
						)}
					</motion.button>
				</TooltipTrigger>
				<TooltipContent>
					<p>
						{name} ({t('tagsSettingsTab.press')} {index + 1})
					</p>
				</TooltipContent>
			</Tooltip>
		))}
		<div className="w-px h-4 bg-border mx-1" />
		<SimpleColorPicker
			value={value}
			onChange={onChange}
			disabled={disabled}
			forceOpen={forceOpenPicker}
			onOpenChange={onPickerOpenChange}
			t={t}
			tagColorPresets={tagColorPresets}
		/>
	</div>
)

const SimpleColorPicker = ({
	value,
	onChange,
	disabled = false,
	forceOpen = false,
	onOpenChange,
	t,
	tagColorPresets,
}: {
	value: string
	onChange: (color: string) => void
	disabled?: boolean
	forceOpen?: boolean
	onOpenChange?: (open: boolean) => void
	t: (key: string) => string
	tagColorPresets: Array<{ color: string; name: string }>
}) => {
	const [open, setOpen] = useState(false)

	// Handle external control of open state
	React.useEffect(() => {
		if (forceOpen) {
			setOpen(true)
		}
	}, [forceOpen])

	const handleOpenChange = (newOpen: boolean) => {
		setOpen(newOpen)
		onOpenChange?.(newOpen)
	}

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>
				<Tooltip>
					<TooltipTrigger asChild>
						<motion.button
							className="w-6 h-6 rounded-full border-2 border-border hover:border-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm relative overflow-hidden group"
							style={{ backgroundColor: value }}
							disabled={disabled}
							whileHover={{ scale: 1.1 }}
							whileTap={{ scale: 0.95 }}>
							<div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
							<Palette className="w-3 h-3 text-white/80 group-hover:text-white transition-colors" />
						</motion.button>
					</TooltipTrigger>
					<TooltipContent>
						<p>{t('tagsSettingsTab.moreColors')} ({t('tagsSettingsTab.press')} M)</p>
					</TooltipContent>
				</Tooltip>
			</PopoverTrigger>
			<PopoverContent
				className="w-80 p-6 bg-gradient-to-br from-background to-muted/50 border-2"
				align="start">
				<div className="space-y-6">
					<div className="text-center">
						<h4 className="text-lg font-semibold flex items-center justify-center gap-2">
							<Sparkles className="w-5 h-5 text-primary" />
							{t('tagsSettingsTab.chooseAColor')}
						</h4>
						<p className="text-sm text-muted-foreground mt-1">
							{t('tagsSettingsTab.pickAColorThatRepresents')}
						</p>
					</div>
					<div className="grid grid-cols-4 gap-4">
						{tagColorPresets.map(({ color, name }) => (
							<motion.button
								key={color}
								className={cn(
									'group relative w-16 h-16 rounded-2xl border-3 transition-all duration-300 flex flex-col items-center justify-center overflow-hidden',
									value === color
										? 'border-primary ring-4 ring-primary/20 shadow-xl scale-105'
										: 'border-border hover:border-primary/50 hover:shadow-lg hover:scale-102'
								)}
								style={{ backgroundColor: color }}
								onClick={() => {
									onChange(color)
									handleOpenChange(false)
								}}
								whileHover={{ y: -2 }}
								whileTap={{ scale: 0.95 }}>
								<div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
								{value === color && (
									<motion.div
										initial={{ scale: 0 }}
										animate={{ scale: 1 }}
										className="relative z-10">
										<Check className="w-6 h-6 text-white drop-shadow-lg" />
									</motion.div>
								)}
								<div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 text-xs font-medium text-white/90 opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 px-2 py-1 rounded-full backdrop-blur-sm">
									{name}
								</div>
							</motion.button>
						))}
					</div>
				</div>
			</PopoverContent>
		</Popover>
	)
}

export function TagsSettingsTab() {
	const { t } = useTranslation()
	const { currentProgram } = useProgramContext()
	const queryClient = useQueryClient()
	const { toast } = useToast()
	const inputRef = useRef<HTMLInputElement>(null)
	const tagColorPresets = getTagColorPresets(t)

	const [newTagName, setNewTagName] = useState('')
	const [newTagColor, setNewTagColor] = useState(tagColorPresets[0].color)
	const [showNewTagForm, setShowNewTagForm] = useState(false)
	const [quickMode, setQuickMode] = useState(false)
	const [openColorPicker, setOpenColorPicker] = useState(false)

	// State for editing an existing tag
	const [editingTag, setEditingTag] = useState<Tag | null>(null)

	// Auto-focus input when form opens
	useEffect(() => {
		if (showNewTagForm && inputRef.current) {
			inputRef.current.focus()
		}
	}, [showNewTagForm])

	useEffect(() => {
		if (newTagName && quickMode) {
			const suggestedColor = getSmartColorSuggestion(newTagName, tagColorPresets)
			setNewTagColor(suggestedColor)
		}
	}, [newTagName, quickMode, tagColorPresets])

	// Keyboard shortcuts
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			// Handle Cmd+N globally (not in inputs)
			if (
				e.key === 'n' &&
				(e.metaKey || e.ctrlKey) &&
				!(e.target instanceof HTMLInputElement)
			) {
				e.preventDefault()
				setShowNewTagForm(true)
				setQuickMode(true)
				return
			}

			// Handle shortcuts when form is open
			if (showNewTagForm && !editingTag) {
				const num = parseInt(e.key)
				if (num >= 1 && num <= 6) {
					e.preventDefault()
					setNewTagColor(tagColorPresets[num - 1].color)
					return
				}

				// 'M' for more colors - allow even when typing in input
				if (
					e.key.toLowerCase() === 'm' &&
					!e.metaKey &&
					!e.ctrlKey &&
					!e.altKey
				) {
					e.preventDefault()
					setOpenColorPicker(true)
					return
				}
			}
		}

		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [showNewTagForm, editingTag])

	// Fetch tags
	const { data: tags = [], isLoading } = useQuery<Tag[]>({
		queryKey: ['tags', currentProgram?.id],
		queryFn: () => apiClient(`/api/tags?programId=${currentProgram?.id}`),
		enabled: !!currentProgram?.id,
	})

	// Mutations
	const createMutation = useMutation({
		mutationFn: (newTag: { name: string; color: string }) =>
			apiClient('/api/tags', {
				method: 'POST',
				data: { ...newTag, program_id: currentProgram?.id },
			}),
		onSuccess: () => {
			toast({
				title: t('tagsSettingsTab.tagCreatedSuccessfully'),
				description: t('tagsSettingsTab.newTagReadyToUse'),
			})
			setNewTagName('')
			setNewTagColor(tagColorPresets[0].color)
			setShowNewTagForm(false)
			setQuickMode(false)
			queryClient.invalidateQueries({ queryKey: ['tags', currentProgram?.id] })
		},
		onError: (error: any) =>
			toast({
				title: t('tagsSettingsTab.errorCreatingTag'),
				description: error.message,
				variant: 'destructive',
			}),
	})

	const updateMutation = useMutation({
		mutationFn: (tag: Tag) =>
			apiClient(`/api/tags/${tag.id}`, {
				method: 'PUT',
				data: { name: tag.name, color: tag.color },
			}),
		onSuccess: () => {
			toast({
				title: t('tagsSettingsTab.tagUpdatedSuccessfully'),
				description: t('tagsSettingsTab.changesHaveBeenSaved'),
			})
			setEditingTag(null)
			queryClient.invalidateQueries({ queryKey: ['tags', currentProgram?.id] })
		},
		onError: (error: any) =>
			toast({
				title: t('tagsSettingsTab.errorUpdatingTag'),
				description: error.message,
				variant: 'destructive',
			}),
	})

	const deleteMutation = useMutation({
		mutationFn: (tagId: string) =>
			apiClient(`/api/tags/${tagId}`, { method: 'DELETE' }),
		onSuccess: () => {
			toast({
				title: t('tagsSettingsTab.tagDeletedSuccessfully'),
				description: t('tagsSettingsTab.tagHasBeenRemoved'),
			})
			queryClient.invalidateQueries({ queryKey: ['tags', currentProgram?.id] })
		},
		onError: (error: any) =>
			toast({
				title: t('tagsSettingsTab.errorDeletingTag'),
				description: error.message,
				variant: 'destructive',
			}),
	})

	// Event handlers
	const handleCreateTag = () => {
		if (!newTagName.trim()) return
		createMutation.mutate({ name: newTagName.trim(), color: newTagColor })
	}

	const handleUpdateTag = () => {
		if (!editingTag || !editingTag.name.trim()) return
		updateMutation.mutate(editingTag)
	}

	const handleEditTag = (tag: Tag) => {
		setEditingTag({ ...tag })
	}

	const handleCancelEdit = () => {
		setEditingTag(null)
	}

	const handleQuickCreate = () => {
		setShowNewTagForm(true)
		setQuickMode(true)
		setNewTagColor(tagColorPresets[Math.floor(Math.random() * 4)].color)
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			if (editingTag) {
				handleUpdateTag()
			} else if (showNewTagForm) {
				handleCreateTag()
			}
		} else if (e.key === 'Escape') {
			if (editingTag) {
				handleCancelEdit()
			} else if (showNewTagForm) {
				setShowNewTagForm(false)
				setNewTagName('')
				setQuickMode(false)
			}
		}
	}

	if (isLoading) {
		return (
			<div className="flex flex-col items-center justify-center py-16 space-y-4">
				<div className="relative">
					<Loader2 className="h-8 w-8 animate-spin text-primary" />
					<div className="absolute inset-0 h-8 w-8 rounded-full border-primary/20 animate-pulse" />
				</div>
			</div>
		)
	}

	return (
		<TooltipProvider>
			<div className="space-y-8 pb-8">
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					className="relative">
					<div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl" />
					<div className="relative p-8 bg-background/50 backdrop-blur-sm rounded-2xl border border-border/50">
						<div className="flex items-center justify-between">
							<div className="space-y-2">
								<div className="flex items-center gap-3">
									<div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg">
										<TagIcon className="h-6 w-6 text-white" />
									</div>
									<div>
										<h2 className="text-2xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
											{t('tagsSettingsTab.tags')}
										</h2>
										<p className="text-muted-foreground">
											{t('tagsSettingsTab.organizeAndCategorize')}
										</p>
									</div>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<motion.div
									whileHover={{ scale: 1.02 }}
									whileTap={{ scale: 0.98 }}>
									<Button
										onClick={() => {
											setShowNewTagForm(true)
											setQuickMode(false)
										}}
										className="gap-2 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-lg hover:shadow-xl transition-all duration-300 px-6 py-3 h-auto"
										disabled={showNewTagForm}>
										<Plus className="h-4 w-4" />
										{t('tagsSettingsTab.createTag')}
									</Button>
								</motion.div>
							</div>
						</div>

					
					</div>
				</motion.div>

				{/* New Tag Form */}
				<AnimatePresence>
					{showNewTagForm && (
						<motion.div
							initial={{ opacity: 0, height: 0, scale: 0.95 }}
							animate={{ opacity: 1, height: 'auto', scale: 1 }}
							exit={{ opacity: 0, height: 0, scale: 0.95 }}
							transition={{ duration: 0.3, ease: 'easeInOut' }}
							className="overflow-hidden">
							<Card
								className={cn(
									'border-2 shadow-lg backdrop-blur-sm',
									quickMode
										? 'border-dashed border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50'
										: 'border-dashed border-primary/30 bg-gradient-to-br from-primary/5 to-secondary/5'
								)}>
								<CardContent className="p-6">
									<div className="space-y-4">
										{/* Quick mode indicator */}
										{quickMode && (
											<motion.div
												initial={{ opacity: 0, x: -20 }}
												animate={{ opacity: 1, x: 0 }}
												className="flex items-center gap-2 text-amber-700 bg-amber-100 px-3 py-1 rounded-full text-sm font-medium">
												<Zap className="h-4 w-4" />
												{t('tagsSettingsTab.quickCreateMode')}
											</motion.div>
										)}

										<div className="flex items-center gap-4">
											{!quickMode && (
												<QuickColorSelector
													value={newTagColor}
													onChange={setNewTagColor}
													disabled={createMutation.isPending}
													forceOpenPicker={openColorPicker}
													onPickerOpenChange={setOpenColorPicker}
													t={t}
													tagColorPresets={tagColorPresets}
												/>
											)}

											<Input
												ref={inputRef}
												value={newTagName}
												onChange={(e) => setNewTagName(e.target.value)}
												placeholder={
													quickMode
														? t('tagsSettingsTab.typeTagNameColorAutoSelect')
														: t('tagsSettingsTab.enterTagName')
												}
												disabled={createMutation.isPending}
												onKeyDown={handleKeyDown}
												className="flex-1 h-12 text-lg bg-background/50 border-2 border-border/50 focus:border-primary/50 transition-all duration-300"
											/>

											{newTagName && (
												<div className="flex items-center gap-2">
													<span className="text-sm text-muted-foreground">
														{t('tagsSettingsTab.preview')}
													</span>
													<TagPreview name={newTagName} color={newTagColor} t={t} />
												</div>
											)}

											<div className="flex items-center gap-2">
												<motion.div
													whileHover={{ scale: 1.05 }}
													whileTap={{ scale: 0.95 }}>
													<Button
														onClick={handleCreateTag}
														disabled={
															!newTagName.trim() || createMutation.isPending
														}
														size="lg"
														className="gap-2 bg-green-600 hover:bg-green-700 shadow-lg">
														{createMutation.isPending ? (
															<Loader2 className="h-4 w-4 animate-spin" />
														) : (
															<Check className="h-4 w-4" />
														)}
														{quickMode ? t('tagsSettingsTab.create') : t('tagsSettingsTab.save')}
													</Button>
												</motion.div>
												<motion.div
													whileHover={{ scale: 1.05 }}
													whileTap={{ scale: 0.95 }}>
													<Button
														onClick={() => {
															setShowNewTagForm(false)
															setNewTagName('')
															setQuickMode(false)
														}}
														variant="outline"
														size="lg"
														className="gap-2 border-2">
														<X className="h-4 w-4" />
														{t('tagsSettingsTab.cancel')}
													</Button>
												</motion.div>
											</div>
										</div>

										{/* Quick color selection hints */}
										{showNewTagForm && !quickMode && (
											<motion.div
												initial={{ opacity: 0 }}
												animate={{ opacity: 1 }}
												transition={{ delay: 0.2 }}
												className="text-xs text-muted-foreground flex items-center gap-4">
												<span>{t('tagsSettingsTab.press1To6ForQuickColors')}</span>
												<span>•</span>
												<span>{t('tagsSettingsTab.pressMForMoreOptions')}</span>
												<span>•</span>
												<span>{t('tagsSettingsTab.enterToSave')}</span>
											</motion.div>
										)}
									</div>
								</CardContent>
							</Card>
						</motion.div>
					)}
				</AnimatePresence>

				{/* Tags List */}
				<div className="space-y-4">
					<AnimatePresence mode="popLayout">
						{tags.map((tag, index) => (
							<motion.div
								key={tag.id}
								layout
								initial={{ opacity: 0, y: 20, scale: 0.95 }}
								animate={{ opacity: 1, y: 0, scale: 1 }}
								exit={{ opacity: 0, y: -20, scale: 0.95 }}
								transition={{ duration: 0.3, delay: index * 0.05 }}
								className="group relative">
								<div className="absolute inset-0 bg-gradient-to-r from-background to-muted/30 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
								<div className="relative flex items-center justify-between p-6 bg-background/80 backdrop-blur-sm border border-border/50 rounded-2xl hover:border-primary/30 hover:shadow-lg transition-all duration-300">
									{editingTag?.id === tag.id ? (
										<div className="flex items-center gap-4 flex-1">
											<QuickColorSelector
												value={editingTag.color || tagColorPresets[0].color}
												onChange={(color) =>
													setEditingTag({ ...editingTag, color })
												}
												t={t}
												tagColorPresets={tagColorPresets}
											/>
											<Input
												value={editingTag.name}
												onChange={(e) =>
													setEditingTag({
														...editingTag,
														name: e.target.value,
													})
												}
												className="flex-1 h-12 text-lg bg-background/50 border-2 border-border/50 focus:border-primary/50"
												autoFocus
												onKeyDown={handleKeyDown}
											/>
											<div className="flex items-center gap-2">
												<motion.div
													whileHover={{ scale: 1.05 }}
													whileTap={{ scale: 0.95 }}>
													<Button
														size="sm"
														variant="ghost"
														onClick={handleUpdateTag}
														disabled={updateMutation.isPending}
														className="h-10 w-10 rounded-xl hover:bg-green-100 hover:text-green-700 transition-colors">
														{updateMutation.isPending ? (
															<Loader2 className="h-4 w-4 animate-spin" />
														) : (
															<Check className="h-4 w-4" />
														)}
													</Button>
												</motion.div>
												<motion.div
													whileHover={{ scale: 1.05 }}
													whileTap={{ scale: 0.95 }}>
													<Button
														size="sm"
														variant="ghost"
														onClick={handleCancelEdit}
														className="h-10 w-10 rounded-xl hover:bg-red-100 hover:text-red-700 transition-colors">
														<X className="h-4 w-4" />
													</Button>
												</motion.div>
											</div>
										</div>
									) : (
										// Display mode
										<>
											<div className="flex items-center gap-4">
												<ColorDot color={tag.color} />
												<span className="font-semibold text-lg text-foreground group-hover:text-primary transition-colors">
													{tag.name}
												</span>
											</div>
											<div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
												<motion.div
													whileHover={{ scale: 1.05 }}
													whileTap={{ scale: 0.95 }}>
													<Button
														size="sm"
														variant="ghost"
														onClick={() => handleEditTag(tag)}
														className="h-10 w-10 rounded-xl text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all">
														<Edit2 className="h-4 w-4" />
													</Button>
												</motion.div>
												<AlertDialog>
													<AlertDialogTrigger asChild>
														<motion.div
															whileHover={{ scale: 1.05 }}
															whileTap={{ scale: 0.95 }}>
															<Button
																size="sm"
																variant="ghost"
																className="h-10 w-10 rounded-xl text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-all">
																<Trash2 className="h-4 w-4" />
															</Button>
														</motion.div>
													</AlertDialogTrigger>
													<AlertDialogContent className="max-w-md">
														<AlertDialogHeader>
															<AlertDialogTitle className="flex items-center gap-2">
																<Trash2 className="h-5 w-5 text-red-600" />
																{t('tagsSettingsTab.deleteTag')}
															</AlertDialogTitle>
															<AlertDialogDescription className="text-base">
																{t('tagsSettingsTab.deleteTagConfirmation', { tagName: tag.name })}
															</AlertDialogDescription>
														</AlertDialogHeader>
														<AlertDialogFooter className="gap-2">
															<AlertDialogCancel className="px-6">
																{t('tagsSettingsTab.cancel')}
															</AlertDialogCancel>
															<AlertDialogAction
																onClick={() => deleteMutation.mutate(tag.id)}
																className="bg-red-600 hover:bg-red-700 px-6">
																{t('tagsSettingsTab.deleteTag')}
															</AlertDialogAction>
														</AlertDialogFooter>
													</AlertDialogContent>
												</AlertDialog>
											</div>
										</>
									)}
								</div>
							</motion.div>
						))}
					</AnimatePresence>

					{tags.length === 0 && (
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.2 }}
							className="text-center py-16">
							<div className="relative mx-auto w-24 h-24 mb-6">
								<div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl animate-pulse" />
								<div className="relative w-full h-full bg-gradient-to-br from-muted to-muted/50 rounded-2xl flex items-center justify-center">
									<TagIcon className="h-8 w-8 text-muted-foreground/50" />
								</div>
							</div>
							<h3 className="text-xl font-semibold text-foreground mb-2">
								{t('tagsSettingsTab.noTagsYet')}
							</h3>
							<p className="text-muted-foreground max-w-md mx-auto mb-6">
								{t('tagsSettingsTab.createYourFirstTagExtended')}
							</p>
						</motion.div>
					)}
				</div>
			</div>
		</TooltipProvider>
	)
}
